import React, { useState, useEffect, useCallback } from 'react';
import axios from '../../api/api';
import { MdOutlineBlock, MdDownload } from 'react-icons/md';
import { FaSearch } from "react-icons/fa";
import Loader from '../Loader';
import Pagination from '../Pagination';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

const Promocode = () => {

  const [promocode, setPromocode] = useState([]);
  const [searchPromocode, setSearchPromocode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [recordsPerPage, setRecordsPerPage] = useState(15);
  const [count, setCount] = useState(0)
  const [total, setTotal] = useState(0)
  const [searched, setSearched] = useState(false);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  // Convert a date to partition format pYYYYMMDD
  const formatDateToPartition = (date) => {
    const d = new Date(date);
    return `p${d.getFullYear()}${String(d.getMonth() + 1).padStart(2, '0')}${String(d.getDate()).padStart(2, '0')}`;
  };

  // Fetch promocode stats
  const getPromocode = useCallback(async (offset, limit,) => {
    setLoading(true);
    setError(null);
    
    try {
      let query = `&offset=${offset}&limit=${limit}`

      if (!searchPromocode) {
        setLoading(false);
        return;
      }

      if (startDate && endDate) {
        const start = new Date(startDate);
        const end = new Date(endDate);

        // Check if endDate is before startDate
        if (end < start) {
          alert('End date cannot be earlier than start date');
          setLoading(false);
          return;
        }
        
        const diffTime = Math.abs(end - start);
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

        if (diffDays > 32) {
          alert('Date range cannot exceed 32 days');
          setLoading(false);
          return;
        }

        const partitions = [];
        let current = new Date(startDate);
        
        // Handle case when start and end are the same
        if (diffDays === 0) {
          let current = new Date(startDate);
          current.setDate(current.getDate() + 1);
          partitions.push(formatDateToPartition(current));
        } else {
          while (current <= end) {
            current.setDate(current.getDate() + 1);
            partitions.push(formatDateToPartition(current));
          }
        }
        query += `&partitions=${partitions.join(',')}`;
      } else if (startDate) {
        const partitions = [];
        let current = new Date(startDate);
        current.setDate(current.getDate() + 1);
        partitions.push(formatDateToPartition(current));
        query += `&partitions=${partitions.join(',')}`;
      }

      const response = await axios.get(`/reports/promocode?promocode=${searchPromocode}${query}`);
      setPromocode(response.data.items);
      setCount(response.data.count)
      setTotal(response.data.total)
      setLoading(false);
      setError(null);
    } catch (error) {
      console.log(error);
      setLoading(false);
      if (!error?.response?.status) {
        setError('Network error! Check your connection.');
      } else if (error.response.status === 400 || error.response.status === 404) {
        setError(error.response.data?.detail || 'Invalid request or resource not found');
      } else if (error.response.status === 500) {
        setError(error.response.data?.detail || 'Server error');
      } else {
        setError(error.response.data?.detail || 'Oops! Something went wrong! Try Again');
      }
    } finally {
      setLoading(false);
    }
  }, [searchPromocode, startDate, endDate]);


  // Handle search button click
  const handleSearch = () => {
    setSearched(true);
    getPromocode((currentPage - 1) * recordsPerPage, recordsPerPage);
  };

  // Handle clearing search inputs
  const handleClearSearch = () => {
    setSearchPromocode('');
    setStartDate('');
    setEndDate('');
    setPromocode([]); 
    setSearched(false);
    setRecordsPerPage(15)
    setCurrentPage(1)
  };

   useEffect(() => {
    const timerId = setTimeout(() => {
      setLoading(true);
      getPromocode((currentPage - 1) * recordsPerPage, recordsPerPage, searchPromocode);
    }, 1000);
    return () => clearTimeout(timerId);
  }, [currentPage, recordsPerPage, getPromocode, searchPromocode]);

  const downloadExcel = () => {
    if (!promocode.length) return alert('No data to download.');
  
    const worksheet = XLSX.utils.json_to_sheet(promocode);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Promocodes');
  
    const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    const fileData = new Blob([excelBuffer], { type: 'application/octet-stream' });
    saveAs(fileData, `promocode-${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  return (
    <div className='mx-auto p-4'>
      <h3 className="my-2"><b>Promocode</b></h3>
      <div className="bg-white rounded-lg p-4 w-full flex-1 min-w-0">
        <div className='flex flex-wrap gap-2 items-center mb-4'>
          <label htmlFor='search'><span className='hidden'>Search</span>
            <input
              type='text'
              id='search'
              className='px-3 py-1 w-full border bg-[#eef1ff] border-slate-300 placeholder-slate-400 rounded-md focus:outline-none focus:border-[#2b1e3e] focus:ring-[#2b1e3e] focus:ring-1'
              required
              placeholder='Search promocode...'
              value={searchPromocode}
              onChange={(e) => setSearchPromocode(e.target.value)}
            />
          </label>
          <label htmlFor="">
              <span className='hidden'>Start Date</span>
              <input 
                type="date"
                className='px-2 py-1 border bg-[#f2f9ff] border-slate-300 placeholder-slate-400 rounded-md focus:outline-none focus:border-[#2b1e3e] focus:ring-[#2b1e3e] focus:ring-1'
                value={startDate}
                onChange={(e) => {setStartDate(e.target.value)}}
              />
            </label>
            <label htmlFor="">
              <span className='hidden'>End Date</span>
              <input 
                type="date" 
                className='px-2 py-1 border bg-[#f2f9ff] border-slate-300 placeholder-slate-400 rounded-md focus:outline-none focus:border-[#2b1e3e] focus:ring-[#2b1e3e] focus:ring-1'
                value={endDate}
                onChange={(e) => {setEndDate(e.target.value)}}
              />
            </label>
          <button
            onClick={handleSearch}
            className='px-4 py-1 bg-[#2b1e3e] text-white rounded-md'
          >
            Search
          </button>
          <button
            onClick={handleClearSearch}
            className='px-4 py-1 bg-red-500 text-white rounded-md'
          >
            Clear
          </button>
          <button
            onClick={downloadExcel}
            className='flex items-center gap-1 bg-[#134463] text-white px-3 py-1 rounded-md hover:bg-[#134463e8]'
          >
            <MdDownload />
            Export
          </button>
        </div>
        {loading ? (
          <div className='flex items-center justify-center bg-[#eef1ff] h-[70vh]'>
            <Loader />
          </div>
        ) : error ? (
          <div className='bg-[#eef1ff] grid place-items-center h-[70vh]'>
            <div className='grid place-items-center text-red-600 p-4'>
              <h3><MdOutlineBlock /></h3>
              <span>{error}</span>
            </div>
          </div>
        ) : (
          searched ? (
          <div className='overflow-x-auto rounded-lg'>
            {promocode.length > 0
              ? (
                <table className='w-full text-left table-auto'>
                  <thead>
                    <tr className='border-b border-slate-500'>
                      <th className='p-2'>Phone Number</th>
                      <th className='p-2'>Account Number</th>
                      <th className='p-2'>Transaction ID</th>
                      <th className='p-2'>Amount</th>
                      <th className='p-2'>Promocode</th>
                      <th className='p-2'>Provider</th>
                      <th className='p-2 whitespace-nowrap'>Created At</th>
                    </tr>
                  </thead>
                  <tbody>
                    {promocode.map((promo) => (
                      <tr key={promo.id}>
                        <td className='p-2'>{promo.msisdn}</td>
                        <td className='p-2'>{promo.account_no}</td>
                        <td className='p-2'>{promo.tran_id}</td>
                        <td className='p-2'>{promo.amount}</td>
                        <td className='p-2'>{promo.promocode}</td>
                        <td className='p-2'>{promo.provider}</td>
                        <td className='p-2 whitespace-nowrap'>{new Date(promo.date_created).toLocaleString("en-CA", { hour12: false })}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <div className='bg-[#eef1ff] text-[#134463] grid place-items-center h-[70vh]'>
                  <div className='grid place-items-center p-4'>
                    <h3><MdOutlineBlock /></h3>
                    <h4>No Data</h4>
                  </div>
                </div>
              )}
          </div>
          ) : (
            <div className='grid place-items-center h-[70vh]'>
              <div className='text-[#134463] p-4'>
                <h3 className='flex items-center justify-center'><FaSearch /></h3>
                <h4>Search Promocode</h4>
              </div>
            </div>
          )
        )}
        {searched && (
          <div className='flex flex-wrap items-center justify-between mt-4 gap-4'>
            <div>
              <span className='mr-2'>Records per page:</span>
              <select
                className='px-3 py-1 border bg-[#eef1ff] border-slate-300 rounded-md focus:outline-none focus:border-[#2b1e3e] focus:ring-[#2b1e3e] focus:ring-1'
                value={recordsPerPage}
                onChange={(e) => {
                  setRecordsPerPage(parseInt(e.target.value, 10));
                  setCurrentPage(1);
                }}
              >
                <option value='15'>15</option>
                <option value='20'>20</option>
                <option value='50'>50</option>
                <option value='75'>75</option>
                <option value='100'>100</option>
              </select>
            </div>
            <div>
              <span className='font-bold text-[#2b1e3e]'>Showing {count} / {total?.toLocaleString() || 0} records</span>
            </div>
            <Pagination
              nPages={Math.ceil(total / recordsPerPage)}
              currentPage={currentPage}
              setCurrentPage={setCurrentPage}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default Promocode;